import { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useNavigate } from "react-router-dom";
import { balanceGameItems } from "../data/balanceGameItems";
import { buildShuffledIndices } from "../utils/shuffleArray";

type Choice = "left" | "right";
type SelectionHistoryItem = {
  roundIndex: number;
  order: number;
  question: string;
  selectedOption: string;
};

const createBalanceRoundOrder = () => buildShuffledIndices(balanceGameItems.length);

export default function BalanceGamePage() {
  const navigate = useNavigate();
  const [roundOrder, setRoundOrder] = useState<number[]>(() =>
    createBalanceRoundOrder()
  );
  const [currentOrderPosition, setCurrentOrderPosition] = useState(0);
  const [selectedChoice, setSelectedChoice] = useState<Choice | null>(null);
  const [isHistoryExpanded, setIsHistoryExpanded] = useState(false);
  const [selectionHistory, setSelectionHistory] = useState<SelectionHistoryItem[]>(
    []
  );
  const [usedCount, setUsedCount] = useState(
    balanceGameItems.length > 0 ? 1 : 0
  );

  const currentRoundIndex = roundOrder[currentOrderPosition];
  const currentRound = balanceGameItems[currentRoundIndex];
  const isCompleted = usedCount >= balanceGameItems.length;

  const handleChoiceSelect = (choice: Choice) => {
    const nextSelectedOption =
      choice === "left" ? currentRound.leftLabel : currentRound.rightLabel;

    setSelectedChoice(choice);
    setSelectionHistory((previousHistory) => [
      {
        roundIndex: currentRoundIndex,
        order: usedCount,
        question: currentRound.question,
        selectedOption: nextSelectedOption,
      },
      ...previousHistory.filter((item) => item.roundIndex !== currentRoundIndex),
    ]);
  };

  const moveToNextRound = () => {
    if (isCompleted) return;

    setCurrentOrderPosition((value) => value + 1);
    setSelectedChoice(null);
    setUsedCount((value) => value + 1);
  };

  const resetBalanceGame = () => {
    setCurrentOrderPosition(0);
    setSelectedChoice(null);
    setIsHistoryExpanded(false);
    setSelectionHistory([]);
    setUsedCount(balanceGameItems.length > 0 ? 1 : 0);
  };

  const reshuffleBalanceGame = () => {
    setRoundOrder(createBalanceRoundOrder());
    setCurrentOrderPosition(0);
    setSelectedChoice(null);
    setIsHistoryExpanded(false);
    setSelectionHistory([]);
    setUsedCount(balanceGameItems.length > 0 ? 1 : 0);
  };

  return (
    <Container maxWidth="sm">
      <Paper elevation={4} sx={{ p: 3, mt: 4, borderRadius: 3 }}>
        <Stack spacing={3}>
          <Stack spacing={1} textAlign="center">
            <Typography variant="h4">⚖️ 밸런스 게임</Typography>
            <Typography color="text.secondary">
              둘 중 하나를 가볍게 고르고 이유를 이야기해보세요.
            </Typography>
          </Stack>

          <Paper variant="outlined" sx={{ p: 3, borderRadius: 3 }}>
            <Stack spacing={1.5} textAlign="center">
              <Typography variant="body2" color="text.secondary">
                ROUND {usedCount}
              </Typography>
              <Typography variant="h5">{currentRound.question}</Typography>
            </Stack>
          </Paper>

          <Stack spacing={2}>
            <Button
              fullWidth
              size="large"
              variant={selectedChoice === "left" ? "contained" : "outlined"}
              onClick={() => handleChoiceSelect("left")}
              sx={{ py: 2 }}
            >
              A. {currentRound.leftLabel}
            </Button>

            <Button
              fullWidth
              size="large"
              variant={selectedChoice === "right" ? "contained" : "outlined"}
              onClick={() => handleChoiceSelect("right")}
              sx={{ py: 2 }}
            >
              B. {currentRound.rightLabel}
            </Button>
          </Stack>

          <Accordion
            expanded={isHistoryExpanded}
            onChange={(_, expanded) => setIsHistoryExpanded(expanded)}
            disableGutters
            sx={{
              borderRadius: 3,
              bgcolor: "background.default",
              "&:before": { display: "none" },
              overflow: "hidden",
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack spacing={0.25}>
                <Typography variant="subtitle1" fontWeight={600}>
                  선택 기록
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  내가 고른 답변을 다시 확인할 수 있어요.
                </Typography>
              </Stack>
            </AccordionSummary>

            <AccordionDetails>
              {selectionHistory.length > 0 ? (
                <Stack spacing={1.25}>
                  {selectionHistory.map((item) => (
                    <Paper
                      key={item.roundIndex}
                      variant="outlined"
                      sx={{ p: 1.75, borderRadius: 2 }}
                    >
                      <Stack spacing={0.5}>
                        <Typography variant="caption" color="text.secondary">
                          질문 {item.order}
                        </Typography>
                        <Typography fontWeight={600}>{item.question}</Typography>
                        <Typography color="text.secondary">
                          선택: {item.selectedOption}
                        </Typography>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              ) : (
                <Typography color="text.secondary">
                  아직 선택 기록이 없어요. 먼저 마음에 드는 쪽을 골라보세요.
                </Typography>
              )}
            </AccordionDetails>
          </Accordion>

          {isCompleted && (
            <Paper
              variant="outlined"
              sx={{ p: 2.5, borderRadius: 3, textAlign: "center" }}
            >
              <Stack spacing={1}>
                <Typography fontWeight={600}>
                  모든 밸런스 게임을 다 사용했어요.
                </Typography>
                <Typography color="text.secondary">
                  다시 섞어서 처음부터 새 순서로 시작하거나 게임 목록으로
                  돌아갈 수 있어요.
                </Typography>
              </Stack>
            </Paper>
          )}

          <Stack spacing={2.5}>
            {!isCompleted && (
              <Button
                fullWidth
                size="large"
                variant="contained"
                onClick={moveToNextRound}
              >
                다음 밸런스 게임
              </Button>
            )}

            {isCompleted && (
              <Button
                fullWidth
                size="large"
                variant="contained"
                onClick={reshuffleBalanceGame}
              >
                다시 섞어서 처음부터
              </Button>
            )}

            <Stack spacing={1.5} sx={{ pt: !isCompleted ? 0.5 : 0 }}>
              {!isCompleted && (
                <Button
                  fullWidth
                  size="large"
                  variant="outlined"
                  onClick={resetBalanceGame}
                >
                  전체 재시작
                </Button>
              )}

              <Button fullWidth size="large" onClick={() => navigate("/list")}>
                게임 목록으로
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Paper>
    </Container>
  );
}
